"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";

import {
  CoverEdit,
  OrganizerEdit,
  TravellerEdit,
  TURNSTILE_INIT_PATHS,
} from "@/config/app-constant";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "error-callback"?: (error: unknown) => void;
          "expired-callback"?: () => void;
          "unsupported-callback"?: () => void;
          "timeout-callback"?: () => void;
          "before-interactive-callback"?: () => void;
        }
      ) => void;
      isExpired?: () => boolean;
      getResponse?: () => string;
      reset?: () => void;
      execute?: () => void;
    };
  }
}

interface TurnstileCaptchaProps {
  isEnabled: boolean;
  siteKey: string;
  theme?: "light" | "dark" | "auto";
  containerId?: string;
  onSuccess?: (token: string) => void;
  onError?: (error: unknown) => void;
  onExpireToken?: () => void;
  onUnsupportedBrowser?: () => void;
  render?: () => void;
}

export default function TurnstileCaptcha({
  isEnabled = false,
  siteKey,
  theme = "light",
  containerId = "cf-turnstile",
  onSuccess = () => {},
  onError = () => {},
  onExpireToken = () => {},
  onUnsupportedBrowser = () => {},
}: Readonly<TurnstileCaptchaProps>) {
  const renderTurnstile = useCallback(() => {
    window.turnstile?.render(`#${containerId}`, {
      sitekey: siteKey,
      theme,
      callback: onSuccess,
      "error-callback": onError,
      "expired-callback": onExpireToken,
      "unsupported-callback": onUnsupportedBrowser,
    });
  }, [
    containerId,
    onError,
    onExpireToken,
    onSuccess,
    onUnsupportedBrowser,
    siteKey,
    theme,
  ]);
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const normalizedPath = pathName.replace(/(^\/)|(\/$)/g, "");
  const isEditFlow = useMemo(() => {
    if (searchParams.get(CoverEdit)) return true;
    if (searchParams.get(TravellerEdit)) return true;
    if (searchParams.get(OrganizerEdit)) return true;
    return false;
  }, [searchParams]);

  useEffect(() => {
    const isPathMatched = TURNSTILE_INIT_PATHS.includes(normalizedPath);
    if (
      isEnabled &&
      !document.querySelector(`#${containerId} iframe`) &&
      siteKey &&
      typeof siteKey === "string" &&
      !isEditFlow
    ) {
      if (window.turnstile) {
        if (isPathMatched) {
          renderTurnstile();
        }
      } else {
        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        script.async = true;
        script.onload = renderTurnstile;
        document.body.appendChild(script);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedPath, isEditFlow]);

  useEffect(() => {
    const handleCustomEvent = () => {
      // Remove existing iframe and re-render fresh widget
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = "";
      }
      renderTurnstile();
    };
    window.addEventListener("gen-new-turnstile-token", handleCustomEvent);
    return () => {
      window.removeEventListener("gen-new-turnstile-token", handleCustomEvent);
    };
  }, [containerId, renderTurnstile]);

  if (!isEnabled || !siteKey || typeof siteKey !== "string") return null;

  return <div id={containerId} className="cf-turnstile" />;
}
